import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  Box,
  useTheme,
  Typography,
  Drawer,
  IconButton,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import { useUser } from "@clerk/clerk-react";
import { tokens } from "../../theme";
import DraftGridCell from "./DraftGridCell";
import PositionList from "./PositionList";
import { useSettings } from "../../contexts/SettingsContext";
import SearchBar from "../../components/SearchBar";
import DraftboardControls from "../../components/DraftboardControls";
import { RoundHeadersColumn } from "./RoundHeaders";
import {
  gridIndexForPick,
  pickInRoundForCell,
  roundForCell,
} from "./draftGrid";
// import ViewportDebug from "../global/ViewportDebug";
import { getCacheItem, setCacheItem, ONE_DAY } from "../../utils/cache";

// Removed virtualized list experiment (unused)

function Draftboard({
  highlightedPlayer = "",
  onPlayerPoolUpdate,
  onDraftedIdsUpdate,
  onCustomDraftAssignmentsUpdate,
  onManuallyDraftedIdsUpdate,
  onDraftboardControlsUpdate,
}) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { user } = useUser();

  // Helper function to get the correct API base URL
  const getApiBaseUrl = useCallback(() => {
    if (process.env.NODE_ENV === "production") {
      // Always use the hardcoded production URL to avoid environment variable issues
      return "https://weez-nfl-data.vercel.app";
    }
    // In development, return empty string to use the proxy
    return "";
  }, []);

  // Helper function to get API headers with authentication
  const getApiHeaders = useCallback(() => {
    const headers = {
      "Accept": "application/json",
      "Content-Type": "application/json",
    };

    // Add Clerk user headers if available
    if (user) {
      headers["X-Clerk-User-Id"] = user.id || "unknown";
      headers["X-Clerk-Email"] = user.primaryEmailAddress?.emailAddress || "";
      headers["X-Clerk-Username"] = user.username || "";
    }

    // Add the shared API key for backend authentication
    const apiKey = process.env.REACT_APP_API_SHARED_KEY;
    if (apiKey) {
      headers["X-API-KEY"] = apiKey;
    }

    return headers;
  }, [user]);

  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const [drawerState, setDrawerState] = useState('medium'); // 'collapsed', 'medium', 'open'
  const [searchOpen, setSearchOpen] = useState(false);
  const [headerElevated, setHeaderElevated] = useState(false);
  const [highlightedPlayerLocal, setHighlightedPlayerLocal] = useState("");
  const [playerPool, setPlayerPool] = useState([]);
  const [isLoadingPlayers, setIsLoadingPlayers] = useState(true);
  const [draftedIds, setDraftedIds] = useState([]);
  const [draftedPlayers, setDraftedPlayers] = useState([]);
  // Preserve last-known live picks across disconnects
  const [frozenDraftedIds, setFrozenDraftedIds] = useState(() => {
    try {
      const saved = localStorage.getItem("frozenDraftedIds");
      return saved ? JSON.parse(saved) : [];
    } catch (_) {
      return [];
    }
  });
  const [frozenDraftedPlayers, setFrozenDraftedPlayers] = useState(() => {
    try {
      const saved = localStorage.getItem("frozenDraftedPlayers");
      return saved ? JSON.parse(saved) : [];
    } catch (_) {
      return [];
    }
  });
  const [manuallyDraftedIds, setManuallyDraftedIds] = useState(new Set()); // Track manually drafted Sleeper IDs
  const [sortOption, setSortOption] = useState("ecr");
  const [isLiveMode, setIsLiveMode] = useState(true);
  const [draftStatus, setDraftStatus] = useState(null);
  const [isActive, setIsActive] = useState(true);
  const statusIntervalRef = useRef(null);
  const playersIntervalRef = useRef(null);

  const [positionFilters, setPositionFilters] = useState({
    QB: true,
    RB: true,
    WR: true,
    TE: true,
    DST: true, // Updated from DEF to DST
    K: true,
  });

  // Intersection Observer setup
  const observerRef = useRef(null);
  const cellRefs = useRef(new Map());
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // You can add logic here to handle cell visibility
          // For example, loading cell data only when visible
          if (entry.isIntersecting) {
            // Handle cell becoming visible
          }
        });
      },
      {
        root: null,
        rootMargin: "50px",
        threshold: 0.1,
      }
    );

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  // Elevate header with a subtle shadow when content is scrolled
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const handleScroll = () => {
      setHeaderElevated(el.scrollTop > 0);
    };
    // Initialize on mount in case the container is already scrolled
    handleScroll();
    el.addEventListener("scroll", handleScroll);
    return () => {
      el.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const {
    scoringType,
    platform,
    draftId,
    teamCount,
    roundCount,
    handleDraftIdChange,
    positionColors,
    customDraftAssignments,
    recentDraftIds,
    resetDraftboardVersion,
    lastResetOptions,
    liveDraftStatus,
    setLiveDraftStatus,
    setLastPicksUpdatedAt,
    isSuperflex,
  } = useSettings();

  // Remove global debugging function for browser console
  useEffect(() => {
    window.debugDraftAssignment = (
      round,
      position,
      currentTeamCount = teamCount
    ) => {
      // Calculate grid index
      const gridIndex = (round - 1) * currentTeamCount + position - 1;

      // Calculate rendering position
      const renderRound = Math.floor(gridIndex / currentTeamCount) + 1;
      const renderPickInRound = (gridIndex % currentTeamCount) + 1;
      const renderActualPick =
        renderRound % 2 === 0
          ? currentTeamCount + 1 - renderPickInRound
          : renderPickInRound;

      return { gridIndex, renderActualPick };
    };
  }, [teamCount, customDraftAssignments]);
  const [isConnected, setIsConnected] = useState(!!draftId);

  const handleSortChange = useCallback((event) => {
    setSortOption(event.target.value);
  }, []);

  const handlePositionFilterChange = useCallback((position) => {
    setPositionFilters((prev) => ({
      ...prev,
      [position]: !prev[position],
    }));
  }, []);

  const handleLiveModeChange = useCallback(
    (event) => {
      // Only allow changing to live mode if connected
      if (!isConnected) {
        setIsLiveMode(false);
        return;
      }
      setIsLiveMode(event.target.checked);
    },
    [isConnected]
  );

  // Update connection state when draftId changes
  useEffect(() => {
    setIsConnected(!!draftId);
  }, [draftId]);

  // Activity detection
  useEffect(() => {
    const handleActivity = () => {
      setIsActive(true);
    };

    const handleInactivity = () => {
      setIsActive(false);
    };

    let inactivityTimer;

    const resetInactivityTimer = () => {
      clearTimeout(inactivityTimer);
      inactivityTimer = setTimeout(handleInactivity, 30000); // 30 seconds of inactivity
    };

    // Set up activity listeners
    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
    ];
    events.forEach((event) => {
      document.addEventListener(event, handleActivity);
      document.addEventListener(event, resetInactivityTimer);
    });

    // Start initial timer
    resetInactivityTimer();

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity);
        document.removeEventListener(event, resetInactivityTimer);
      });
      clearTimeout(inactivityTimer);
    };
  }, []);

  // Respond to global reset requests from SettingsContext
  useEffect(() => {
    // Clear based on selected options
    if (lastResetOptions?.resetDrafted) {
      setManuallyDraftedIds(new Set());
      // Clear live/frozen picks; live will repopulate if connected
      setDraftedPlayers([]);
      setDraftedIds([]);
      setDraftStatus(null);
      setFrozenDraftedPlayers([]);
      setFrozenDraftedIds([]);
    }
    setHighlightedPlayerLocal("");
    setSearchOpen(false);
  }, [resetDraftboardVersion, lastResetOptions]);

  // Persist frozen arrays to localStorage so they survive remounts/disconnects
  useEffect(() => {
    try {
      localStorage.setItem("frozenDraftedIds", JSON.stringify(frozenDraftedIds));
    } catch (_) {}
  }, [frozenDraftedIds]);
  useEffect(() => {
    try {
      localStorage.setItem("frozenDraftedPlayers", JSON.stringify(frozenDraftedPlayers));
    } catch (_) {}
  }, [frozenDraftedPlayers]);

  const connectLiveDraft = useCallback(() => {
    if (isConnected) {
      // Disconnect
      setIsConnected(false);
      handleDraftIdChange(""); // Clear draft ID
    } else {
      // Connect - show recent draft IDs or prompt for new one
      if (recentDraftIds.length > 0) {
        // Create a custom prompt with recent options
        const recentOptions = recentDraftIds
          .map((id, index) => `${index + 1}. ${id}`)
          .join("\n");
        const message = `Recent Draft IDs:\n${recentOptions}\n\nEnter a number (1-${recentDraftIds.length}) to use a recent ID, or enter a new Draft ID:`;
        const input = prompt(message);

        if (input) {
          const numInput = parseInt(input);
          if (numInput >= 1 && numInput <= recentDraftIds.length) {
            // User selected a recent ID
            const selectedId = recentDraftIds[numInput - 1];
            handleDraftIdChange(selectedId);
            setIsConnected(true);
            console.log(`Connected to draft ID: ${selectedId}`); // Log the result
          } else {
            // User entered a new ID
            handleDraftIdChange(input);
            setIsConnected(true);
            console.log(`Connected to new draft ID: ${input}`); // Log the result
          }
        }
      } else {
        // No recent IDs, use simple prompt
        const id = prompt("Please enter or paste your Sleeper Draft ID.");
        if (id) {
          handleDraftIdChange(id);
          setIsConnected(true);
          console.log(`Connected to draft ID: ${id}`); // Log the result
        }
      }
    }
  }, [isConnected, recentDraftIds, handleDraftIdChange]);

  // Memoize controls for topbar and avoid infinite parent-child update loops
  const controlsSignature = useMemo(
    () =>
      JSON.stringify({
        positionFilters,
        sortOption,
        isLiveMode,
        isConnected,
      }),
    [positionFilters, sortOption, isLiveMode, isConnected]
  );

  const controlsRef = useRef(null);
  controlsRef.current = {
    positionFilters,
    handlePositionFilterChange,
    sortOption,
    handleSortChange,
    isLiveMode,
    isConnected,
    handleLiveModeChange,
    connectLiveDraft,
  };

  useEffect(() => {
    if (!onDraftboardControlsUpdate) return;
    onDraftboardControlsUpdate(controlsRef.current);
    // Do not provide cleanup here to avoid nulling controls on every change
  }, [onDraftboardControlsUpdate, controlsSignature]);

  // Cleanup controls only on unmount
  useEffect(() => {
    return () => {
      if (onDraftboardControlsUpdate) onDraftboardControlsUpdate(null);
    };
  }, [onDraftboardControlsUpdate]);

  // Helper function to determine if a player should be considered drafted
  const isPlayerDrafted = useCallback(
    (playerSleeperId) => {
      // Check if player is custom assigned (keeper)
      const isCustomAssigned = Object.keys(customDraftAssignments).includes(
        playerSleeperId
      );
      const isFrozenDrafted = !isConnected && frozenDraftedIds.includes(playerSleeperId);

      if (isLiveMode) {
        // In live mode, respect live draft status, manual overrides, and custom assignments
        return (
          draftedIds.includes(playerSleeperId) ||
          isFrozenDrafted ||
          manuallyDraftedIds.has(playerSleeperId) ||
          isCustomAssigned
        );
      } else {
        // In static mode, respect live draft status and custom assignments, but allow manual undrafting
        const isLiveDrafted = draftedIds.includes(playerSleeperId) || isFrozenDrafted;
        const isManuallyUndrafted = manuallyDraftedIds.has(playerSleeperId);

        // If live drafted but manually undrafted, show as not drafted
        if (isLiveDrafted && isManuallyUndrafted) {
          return false;
        }

        // Show as drafted if live drafted or custom assigned
        return isLiveDrafted || isCustomAssigned;
      }
    },
    [customDraftAssignments, draftedIds, isLiveMode, manuallyDraftedIds, isConnected, frozenDraftedIds]
  );

  // Memoize handlers
  const handlePlayerDraft = useCallback(
    (playerId) => {
      // Debounce the state update
      requestAnimationFrame(() => {
        setManuallyDraftedIds((prev) => {
          const newSet = new Set(prev);
          const isLiveDrafted = draftedIds.includes(playerId);
          const isCustomAssigned = Object.keys(customDraftAssignments).includes(
            playerId
          );
          const isManuallyUndrafted = newSet.has(playerId);

          if (isLiveDrafted || isCustomAssigned) {
            isManuallyUndrafted
              ? newSet.delete(playerId)
              : newSet.add(playerId);
          } else {
            isManuallyUndrafted
              ? newSet.delete(playerId)
              : newSet.add(playerId);
          }

          onManuallyDraftedIdsUpdate?.(newSet);
          return newSet;
        });
      });
    },
    [draftedIds, customDraftAssignments, onManuallyDraftedIdsUpdate]
  );

  // Move getOrganizedPlayers before useMemo
  // Add after your state and effect declarations, but before the useMemo
  const getOrganizedPlayers = useCallback(() => {
    const ROUNDS = roundCount;
    const TEAMS = teamCount;
    const TOTAL_PICKS = ROUNDS * TEAMS;

    // console.log(`🔍 getOrganizedPlayers: teamCount=${teamCount}, TEAMS=${TEAMS}, TOTAL_PICKS=${TOTAL_PICKS}`);

    // Call debug function once
    if (Object.keys(customDraftAssignments).length > 0) {
      // debugGridMapping();
    }

    // Helper function to calculate the grid index for a given round and team number
    // This ensures consistent snake draft logic across the entire function
    const calculateGridIndex = (round, teamNumber) =>
      (round - 1) * TEAMS + (teamNumber - 1);

    // Initialize grid with nulls
    const grid = new Array(TOTAL_PICKS).fill(null);

    // --- Phase 1: Place players with custom draft assignments ---
    // console.log(`🟠 Draftboard Phase 1: Processing custom assignments:`, customDraftAssignments);

    Object.entries(customDraftAssignments).forEach(([playerId, assignment]) => {
      // console.log(`🟠 Draftboard Phase 1: Processing assignment for playerId=${playerId}:`, assignment);

      const pid = String(playerId);
      const player = playerPool.find(
        (p) => String(p.sleeper_id) === pid || String(p.id) === pid
      );

      if (player) {
        const { round, position } = assignment; // 'position' is the team number (1-12)
        const gridIndex = calculateGridIndex(round, position);

        if (gridIndex >= 0 && gridIndex < TOTAL_PICKS) {
          const isDrafted = isPlayerDrafted(player.sleeper_id);
          grid[gridIndex] = {
            ...player,
            isCustomAssigned: true,
            isDrafted: isDrafted,
          };
        }
      } else {
        console.error(
          `🟠 Draftboard Phase 1: Player not found for playerId=${playerId}`
        );
      }
    });

    // Determine which drafted picks to render: prefer live arrays, fallback to frozen snapshot
    const effectiveDraftedPlayers = draftedPlayers?.length ? draftedPlayers : frozenDraftedPlayers;

    // Phase 2 below places every pick in `effectiveDraftedPlayers`, so Phase 3
    // has to hold back exactly that set. It used to check `draftedIds` instead,
    // which is empty precisely when the live array is -- the case that makes
    // Phase 2 fall back to the frozen snapshot. Those players then stayed in the
    // available pool and Phase 4 dealt each of them a SECOND cell: one player,
    // two cells, duplicate React keys, a board that renders out of order.
    const placedDraftedIds = new Set(
      isLiveMode || !isConnected
        ? effectiveDraftedPlayers.map((draft) => draft.player_id)
        : []
    );

    // In live mode, or when disconnected (show last-known live picks), place drafted players
    if (isLiveMode || !isConnected) {
      // --- Phase 2: Live mode - Place actually drafted players ---
      effectiveDraftedPlayers.forEach((draft) => {
        const draftedPlayer = playerPool.find(
          (player) => player.sleeper_id === draft.player_id
        );
        if (draftedPlayer) {
          const gridIndex = gridIndexForPick(draft.pick_no, TEAMS);

          if (gridIndex >= 0 && gridIndex < TOTAL_PICKS) {
            grid[gridIndex] = { ...draftedPlayer, isDrafted: true };
            // console.log(`Drafted player placed: ${draftedPlayer.name} at grid index ${gridIndex}`); // Log live draft result
          }
        }
      });
    }

    // --- Phase 3: Prepare available players for filling empty slots ---
    const customAssignedIds = Object.keys(customDraftAssignments);
    let availablePlayers = playerPool.filter((player) => {
      const playerId = String(player.sleeper_id || player.id);
      const isManuallyDrafted = manuallyDraftedIds.has(player.sleeper_id);
      const isCustomAssigned = customAssignedIds.includes(playerId);
      const isLiveDrafted = placedDraftedIds.has(player.sleeper_id);

      // Exclude custom assigned players and live drafted players from this pool
      if (isCustomAssigned || isLiveDrafted) {
        return false;
      }

      // Include manually drafted players regardless of position filter
      if (isManuallyDrafted) {
        return true;
      }

      // For other players, respect position filter
      // console.dir(player);
      // console.log(`Player position: ${player.position}`);
      return positionFilters[player.position];
    });

    // Sort available players (live mode path)
    if (sortOption === "ecr") {
      availablePlayers.sort((a, b) => {
        const ra = Number.isFinite(a.ovr_rank_ecr) ? a.ovr_rank_ecr : 999;
        const rb = Number.isFinite(b.ovr_rank_ecr) ? b.ovr_rank_ecr : 999;
        return ra - rb;
      });
    } else if (sortOption === "adp") {
      availablePlayers.sort((a, b) => {
        const ra = Number.isFinite(a.adp_rank) ? a.adp_rank : 999;
        const rb = Number.isFinite(b.adp_rank) ? b.adp_rank : 999;
        if (ra !== rb) return ra - rb;
        const aa = Number.isFinite(a.adp_raw) ? a.adp_raw : 999;
        const ab = Number.isFinite(b.adp_raw) ? b.adp_raw : 999;
        return aa - ab;
      });
    }

    // Only switch to fully static board when connected AND live mode is off
    if (!isLiveMode && isConnected) {
      // In static mode, sort and display all players by ECR or ADP
      availablePlayers = playerPool.filter((player) => {
        const playerId = String(player.sleeper_id || player.id);
        const isCustomAssigned = customAssignedIds.includes(playerId);

        // Exclude custom assigned players
        if (isCustomAssigned) {
          return false;
        }

        // Apply position filter
        return positionFilters[player.position];
      });

      // Sort available players (static mode path)
      if (sortOption === "ecr") {
        availablePlayers.sort((a, b) => {
          const ra = Number.isFinite(a.ovr_rank_ecr) ? a.ovr_rank_ecr : 999;
          const rb = Number.isFinite(b.ovr_rank_ecr) ? b.ovr_rank_ecr : 999;
          return ra - rb;
        });
      } else if (sortOption === "adp") {
        availablePlayers.sort((a, b) => {
          const ra = Number.isFinite(a.adp_rank) ? a.adp_rank : 999;
          const rb = Number.isFinite(b.adp_rank) ? b.adp_rank : 999;
          if (ra !== rb) return ra - rb;
          const aa = Number.isFinite(a.adp_raw) ? a.adp_raw : 999;
          const ab = Number.isFinite(b.adp_raw) ? b.adp_raw : 999;
          return aa - ab;
        });
      }
    }

    // --- Phase 4: Fill remaining empty slots in the grid ---
    let playerIndex = 0;
    for (let round = 1; round <= ROUNDS; round++) {
      const roundStartIndex = (round - 1) * TEAMS;

      if (round % 2 === 0) {
        // Even rounds (snake): Fill from right to left (indices TEAMS-1 down to 0)
        for (let i = TEAMS - 1; i >= 0; i--) {
          const gridIndex = roundStartIndex + i;
          if (grid[gridIndex] === null && availablePlayers[playerIndex]) {
            grid[gridIndex] = availablePlayers[playerIndex];
            playerIndex++;
          }
        }
      } else {
        // Odd rounds: Fill from left to right (indices 0 up to TEAMS-1)
        for (let i = 0; i < TEAMS; i++) {
          const gridIndex = roundStartIndex + i;
          if (grid[gridIndex] === null && availablePlayers[playerIndex]) {
            grid[gridIndex] = availablePlayers[playerIndex];
            playerIndex++;
          }
        }
      }
    }

    return grid;
  }, [
    roundCount,
    teamCount,
    customDraftAssignments,
    playerPool,
    isPlayerDrafted,
    isLiveMode,
    draftedPlayers,
    frozenDraftedPlayers,
    manuallyDraftedIds,
    isConnected,
    positionFilters,
    sortOption,
  ]);

  // Then your existing useMemo
  const organizedPlayers = useMemo(
    () => getOrganizedPlayers(),
    [getOrganizedPlayers]
  );

  // Add functions to fetch draft status and drafted players from Sleeper API
  const fetchDraftStatus = useCallback(async (id) => {
    try {
      const response = await fetch(`https://api.sleeper.app/v1/draft/${id}`);
      const data = await response.json();
      const status = data?.status ?? null;
      // Only update if status actually changed to avoid extra renders
      setDraftStatus((prev) => (prev !== status ? status : prev));
      if (status && status !== liveDraftStatus) setLiveDraftStatus(status);
      return status;
    } catch (error) {
      console.error(
        `[${new Date().toLocaleTimeString()}] ❌ Error fetching draft status:`,
        error
      );
      return null;
    }
  }, [liveDraftStatus, setLiveDraftStatus]);

  const fetchDraftedPlayers = useCallback(
    async (id) => {
      try {
        const response = await fetch(
          `https://api.sleeper.app/v1/draft/${id}/picks`
        );
        const data = await response.json();
        // Update when length or last pick changes to reflect rapid live updates
        setDraftedPlayers((previousPicks) => {
          const previousLength = previousPicks?.length || 0;
          const nextLength = data?.length || 0;
          const previousLast = previousLength
            ? previousPicks[previousLength - 1]
            : null;
          const nextLast = nextLength ? data[nextLength - 1] : null;
          const lengthChanged = previousLength !== nextLength;
          const lastChanged =
            previousLast?.player_id !== nextLast?.player_id ||
            previousLast?.pick_no !== nextLast?.pick_no;

          if (lengthChanged || lastChanged) {
            console.log(
              `[${new Date().toLocaleTimeString()}] Drafted players updated (len ${previousLength}→${nextLength}, last ${
                previousLast?.player_id
              }→${nextLast?.player_id})`
            );
            // Keep frozen snapshot in sync when we have data
            if (Array.isArray(data) && data.length > 0) {
              setFrozenDraftedPlayers(data);
            }
            return data;
          }
          return previousPicks;
        });

        // Keep draftedIds in sync with minimal updates
        const nextIds = data.map((draft) => draft.player_id);
        setDraftedIds((previousIds) => {
          const previousLength = previousIds?.length || 0;
          const nextLength = nextIds.length;
          const lengthChanged = previousLength !== nextLength;
          const previousLast = previousLength
            ? previousIds[previousLength - 1]
            : undefined;
          const nextLast = nextLength ? nextIds[nextLength - 1] : undefined;
          const lastChanged = previousLast !== nextLast;
          const updated = lengthChanged || lastChanged ? nextIds : previousIds;
          if (updated && updated.length > 0) {
            setFrozenDraftedIds(updated);
          }
          return updated;
        });
        onDraftedIdsUpdate?.(nextIds);
        // Update last picks updated timestamp
        setLastPicksUpdatedAt(Date.now());
      } catch (error) {
        console.error(
          `[${new Date().toLocaleTimeString()}] ❌ Error fetching drafted players:`,
          error
        );
      }
    },
    [onDraftedIdsUpdate, setLastPicksUpdatedAt]
  );

  // Initial fetch when connecting or changing draft ID
  useEffect(() => {
    if (!draftId || !isConnected) {
      // Cleanup any existing intervals if disconnecting
      if (statusIntervalRef.current) clearInterval(statusIntervalRef.current);
      if (playersIntervalRef.current) clearInterval(playersIntervalRef.current);
      statusIntervalRef.current = null;
      playersIntervalRef.current = null;
      return;
    }

    fetchDraftStatus(draftId);
    fetchDraftedPlayers(draftId);
  }, [draftId, isConnected, fetchDraftStatus, fetchDraftedPlayers]);

  // Fetch player pool from local API
  useEffect(() => {
    let aborted = false;
    const fetchPlayerPool = async () => {
      try {
        const cacheKey = `players:v2:${platform}:${scoringType}:sf:${isSuperflex ? '1' : '0'}`;
        const cached = getCacheItem(cacheKey);
        if (Array.isArray(cached) && cached.length > 0) {
          if (!aborted) {
            setPlayerPool(cached);
            onPlayerPoolUpdate?.(cached);
            setIsLoadingPlayers(false);
          }
          return; // Serve from cache and skip network
        }
        const response = await fetch(
          `${getApiBaseUrl()}/api/players/?live_draft=true&scoring_type=${scoringType}&superflex=${isSuperflex ? 'true' : 'false'}`,
          { headers: getApiHeaders() }
        );
        if (!response.ok) {
          const text = await response.text();
          throw new Error(`Upstream ${response.status} ${response.statusText}: ${text.slice(0,200)}`);
        }
        const respClone = response.clone();
        const data = await response.json();
        // Normalize to an array regardless of shape
        const rawArray = Array.isArray(data)
          ? data
          : Array.isArray(data?.results)
          ? data.results
          : Array.isArray(data?.players)
          ? data.players
          : Array.isArray(data?.data)
          ? data.data
          : data && typeof data === 'object'
          ? Object.values(data)
          : [];
        // const sample = rawArray[0] || null;
        if (rawArray.length === 0) {
          try {
            const debugText = await respClone.text();
            console.warn('[players] empty payload', {
              status: response.status,
              upstream: response.headers.get('x-proxy-upstream'),
              upstreamStatus: response.headers.get('x-proxy-upstream-status'),
              contentType: response.headers.get('content-type'),
              bodySnippet: debugText.slice(0, 200)
            });
          } catch (_) {}
        }
        const scoringData = scoringType === "standard" ? "non_ppr" : scoringType;
        const transformedPlayers = rawArray.map((player) => {
          const name =
            player.name ||
            player.player_name ||
            [player.first_name, player.last_name].filter(Boolean).join(' ') ||
            '';
          const fantasyPoints =
            player.projections?.fantasy_points?.[scoringType] ??
            player[`pts_${scoringType === 'ppr' ? 'ppr' : scoringType === 'half_ppr' ? 'half_ppr' : 'std'}`] ??
            player.pts_cust ??
            0;
          // Prefer superflex ranking buckets when enabled and present, else fall back to standard buckets
          const superflexKey = `superflex_${scoringData}`;
          const rankingsSF = player?.[superflexKey] || player?.fantasy_data?.[superflexKey] || null;
          const rankingsStd = player?.fantasy_data?.[scoringData] || null;
          const rankings = (isSuperflex && rankingsSF) ? rankingsSF : (rankingsStd || {});
          const ovrRank = rankings?.ovr_rank ?? player?.fantasy_data?.[scoringData]?.ovr_rank;
          const posRank = rankings?.pos_rank ?? player?.fantasy_data?.[scoringData]?.pos_rank;
          const ovrTier = rankings?.ovr_tier ?? player?.fantasy_data?.[scoringData]?.ovr_tier;
          const posTier = rankings?.pos_tier ?? player?.fantasy_data?.[scoringData]?.pos_tier;
          const adpRank = rankings?.adp_rank ?? player?.fantasy_data?.[scoringData]?.adp_rank;
          const adpRaw = rankings?.adp_raw ?? player?.fantasy_data?.[scoringData]?.adp_raw;
          return {
            id: player.id || player._id || player.player_id || name,
            sleeper_id:
              player.external_ids?.sleeper_id ??
              player.external_ids?.sleeper ??
              player.sleeper_id ??
              null,
            name,
            position: player.position || player.pos || '',
            team: player.team || player.team_abbr || player.nfl_team || '',
            bye_week: player.bye_week || player.bye || 0,
            ovr_rank_ecr: Number(ovrRank ?? 999) || 999,
            pos_rank_ecr: Number(posRank ?? 999) || 999,
            ovr_tier: Number(ovrTier ?? 99) || 99,
            pos_tier: Number(posTier ?? 99) || 99,
            adp_rank: Number(adpRank ?? 999) || 999,
            adp_raw: Number(adpRaw ?? 999) || 999,
            fantasy_points: fantasyPoints,
          };
        });
        if (!aborted) {
          setPlayerPool(transformedPlayers);
          onPlayerPoolUpdate?.(transformedPlayers);
          setIsLoadingPlayers(false);
        }
        // Write to cache after successful transform
        setCacheItem(cacheKey, transformedPlayers, ONE_DAY);
      } catch (error) {
        if (!aborted) {
          console.error("Error fetching player pool:", error);
          setIsLoadingPlayers(false);
        }
      }
    };
    fetchPlayerPool();
    return () => {
      aborted = true;
    };
  }, [scoringType, platform, isSuperflex, onPlayerPoolUpdate, getApiBaseUrl, getApiHeaders]);

  // Manage polling intervals based on status and activity
  useEffect(() => {
    if (!draftId || !isConnected) return;

    // Clear existing intervals before setting new ones
    if (statusIntervalRef.current) clearInterval(statusIntervalRef.current);
    if (playersIntervalRef.current) clearInterval(playersIntervalRef.current);
    statusIntervalRef.current = null;
    playersIntervalRef.current = null;

    if (draftStatus === "complete") {
      console.log(
        `[${new Date().toLocaleTimeString()}] Polling disabled: draft is complete`
      );
      return; // No polling when complete
    }

    let statusIntervalMs;
    let playersIntervalMs = isActive ? 1000 : 10000;

    if (draftStatus === "drafting" || draftStatus === "paused") {
      statusIntervalMs = isActive ? 1000 : 5000;
    } else if (draftStatus === "pre_draft") {
      statusIntervalMs = isActive ? 10000 : 30000;
    } else {
      // Unknown status, use conservative polling
      statusIntervalMs = isActive ? 10000 : 30000;
      console.log("Draft status is unknown");
    }
    console.log(
      `[${new Date().toLocaleTimeString()}] Polling setup → status: ${statusIntervalMs}ms, players: ${playersIntervalMs}ms (status: ${draftStatus}, active: ${
        isActive ? "yes" : "no"
      })`
    );

    statusIntervalRef.current = setInterval(
      () => fetchDraftStatus(draftId),
      statusIntervalMs
    );
    playersIntervalRef.current = setInterval(
      () => fetchDraftedPlayers(draftId),
      playersIntervalMs
    );

    return () => {
      if (statusIntervalRef.current) clearInterval(statusIntervalRef.current);
      if (playersIntervalRef.current) clearInterval(playersIntervalRef.current);
      statusIntervalRef.current = null;
      playersIntervalRef.current = null;
    };
  }, [
    draftId,
    isConnected,
    draftStatus,
    isActive,
    fetchDraftStatus,
    fetchDraftedPlayers,
  ]);

  // Sort and organize players
  // Calculate tier counts for scarcity indicators
  const tierCounts = useMemo(() => {
    const counts = {};
    const customAssignedIds = Object.keys(customDraftAssignments);

    playerPool
      .filter((player) => {
        const playerId = String(player.sleeper_id || player.id);
        return (
          !isPlayerDrafted(player.sleeper_id) &&
          !customAssignedIds.includes(playerId) &&
          positionFilters[player.position]
        );
      })
      .forEach((player) => {
        const tier = player.pos_tier || 99;
        counts[tier] = (counts[tier] || 0) + 1;
      });

    return counts;
  }, [customDraftAssignments, isPlayerDrafted, playerPool, positionFilters]);

  // Function to get position-specific players
  const getPositionPlayers = (position) => {
    const customAssignedIds = Object.keys(customDraftAssignments);

    return playerPool
      .filter((player) => {
        const playerId = String(player.sleeper_id || player.id);
        return (
          player.position === position &&
          !isPlayerDrafted(player.sleeper_id) &&
          !customAssignedIds.includes(playerId)
        ); // Exclude custom assigned players
      })
      .sort((a, b) => {
        // Sort by position rank
        const rankA = a.pos_rank_ecr || 999;
        const rankB = b.pos_rank_ecr || 999;
        return rankA - rankB;
      });
  };

  // Removed unused experimental components to reduce bundle size

  // Removed unused experimental click handler

  // Define callbacks for search functionality
  const handleHighlightPlayer = useCallback((playerName) => {
    setHighlightedPlayerLocal(playerName);
  }, []);

  const handlePlayerSelected = useCallback((playerName) => {
    if (playerName) {
      setSearchOpen(false);
    }
  }, []);

  const effectiveHighlighted = highlightedPlayerLocal || highlightedPlayer;

  // Show loading screen while players are being fetched
  if (isLoadingPlayers) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
          gap: 2,
        }}
      >
        <CircularProgress size={60} sx={{ color: colors.blueAccent[500] }} />
        <Typography variant="h6" sx={{ color: colors.grey[300] }}>
          Loading draftboard...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      className="draftboard-page"
      sx={{
        mt: { xs: "10px", md: "16px", lg: "12px", xl: "20px" },
        mr: { xs: "10px", md: "16px", lg: "8px", xl: "20px" },
        mb: 0,
        ml: { xs: "10px", md: "16px", lg: "6px", xl: "20px" },
        display: "flex",
        flexDirection: "column",
        flex: "1 1 auto",
        minHeight: 0,
      }}
    >
      {/* <ViewportDebug /> */}

      <Box //draftboard layout
        className="draftboard-layout"
        mt="20px"
        display="flex"
        gap={1}
        sx={{
          overflow: "hidden",
          width: "100%",
          flexDirection: "column",
          flex: '1 1 auto',
          minHeight: 0,
        }}
      >
        {/* Main Draftboard */}
        <Box  //draftboard-main
          className="draftboard-main"
          ref={scrollContainerRef}
          sx={{
            flex: "1 1 auto",
            minWidth: 0,
            minHeight: 0,
            overflowX: "auto",
            overflowY: "auto",

            "& .draftboard-container": {
              padding: "16px 20px 16px 16px",
              backgroundColor: colors.primary[400],
              borderRadius: "8px",
              minWidth: "fit-content",
            },
            "& .draftboard-grid": {
              display: "grid",
              gridTemplateColumns: {
                xs: `repeat(${teamCount}, minmax(${
                  teamCount <= 8
                    ? "140px"
                    : teamCount <= 10
                    ? "120px"
                    : teamCount <= 12
                    ? "95px"
                    : "75px"
                }, 1fr))`,
                sm: `repeat(${teamCount}, minmax(${
                  teamCount <= 8
                    ? "140px"
                    : teamCount <= 10
                    ? "120px"
                    : teamCount <= 12
                    ? "95px"
                    : "75px"
                }, 1fr))`,
                md: `repeat(${teamCount}, minmax(${
                  teamCount <= 8
                    ? "150px"
                    : teamCount <= 10
                    ? "130px"
                    : teamCount <= 12
                    ? "100px"
                    : "80px"
                }, 1fr))`,
                lg: `repeat(${teamCount}, minmax(${
                  teamCount <= 8
                    ? "115px"
                    : teamCount <= 10
                    ? "100px"
                    : teamCount <= 12
                    ? "85px"
                    : "64px"
                }, 1fr))`,
                xl: `repeat(${teamCount}, minmax(${
                  teamCount <= 8
                    ? "115px"
                    : teamCount <= 10
                    ? "100px"
                    : teamCount <= 12
                    ? "85px"
                    : "64px"
                }, 1fr))`,
              },
              gridAutoRows: {
                xs: "68px",
                sm: "72px",
                md: "76px",
                lg: "76px",
                xl: "76px",
              },
              gap: { xs: "1px", sm: "1px", md: "1px", lg: "1px", xl: "1px" },
            },
          }}
        >
          <Box className="draftboard-container">
            {/* Team Numbers Row */}
            <Box
              className="draftboard-header"
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 3,
                display: "flex",
                mb: 1,
                backgroundColor: colors.primary[400],
                boxShadow: headerElevated
                  ? "0 4px 10px rgba(0,0,0,0.25)"
                  : "none",
                transition: "box-shadow 150ms ease",
              }}
            >
              {/* Space for round numbers column */}
              <Box
                className="header-spacer"
                sx={{
                  width: { xs: "14px", md: "18px", lg: "8px", xl: "24px" },
                  mr: { xs: 0.5, lg: 0.5, xl: 1 },
                }}
              />

              {/* Team numbers grid */}
              <Box
                className="team-headers-grid"
                sx={{
                  flex: 1,
                  display: "grid",
                  gridTemplateColumns: {
                    xs: `repeat(${teamCount}, minmax(${
                      teamCount <= 10
                        ? "75px"
                        : teamCount <= 12
                        ? "65px"
                        : "55px"
                    }, 1fr))`,
                    sm: `repeat(${teamCount}, minmax(${
                      teamCount <= 10
                        ? "80px"
                        : teamCount <= 12
                        ? "68px"
                        : "58px"
                    }, 1fr))`,
                    md: `repeat(${teamCount}, minmax(${
                      teamCount <= 10
                        ? "90px"
                        : teamCount <= 12
                        ? "75px"
                        : "65px"
                    }, 1fr))`,
                    lg: `repeat(${teamCount}, minmax(${
                      teamCount <= 10
                        ? "75px"
                        : teamCount <= 12
                        ? "65px"
                        : "55px"
                    }, 1fr))`,
                    xl: `repeat(${teamCount}, minmax(${
                      teamCount <= 10
                        ? "90px"
                        : teamCount <= 12
                        ? "75px"
                        : "65px"
                    }, 1fr))`,
                  },
                  gap: {
                    xs: "1px",
                    sm: "2px",
                    md: "3px",
                    lg: "3px",
                    xl: "4px",
                  },
                }}
              >
                {Array.from({ length: teamCount }, (_, i) => (
                  <Typography
                    key={i}
                    variant="body2"
                    sx={{
                      color: colors.grey[100],
                      textAlign: "center",
                      fontSize: "12px",
                      fontWeight: "bold",
                      backgroundColor: colors.primary[400],
                      padding: "4px",
                      borderRadius: "4px",
                      width: "100%",
                    }}
                  >
                    Team {i + 1}
                  </Typography>
                ))}
              </Box>
            </Box>

            {/* Main Grid with Round Numbers */}
            <Box className="draftboard-grid-container" sx={{ display: "flex" }}>
              {/* Round Numbers Column */}
              {/* */}
              <RoundHeadersColumn roundCount={roundCount} />

              {/* Draftboard Grid */}
              <div className="draftboard-grid" style={{ flex: 1 }}>
                {organizedPlayers.map((player, index) => {
                  const round = roundForCell(index, teamCount);
                  const actualPick = pickInRoundForCell(index, teamCount);

                  return (
                    <DraftGridCell
                      key={`cell-${index}`}
                      ref={(el) => {
                        const previous = cellRefs.current.get(index);
                        if (previous && observerRef.current) {
                          observerRef.current.unobserve(previous);
                        }
                        if (el) {
                          cellRefs.current.set(index, el);
                          observerRef.current?.observe(el);
                        } else {
                          cellRefs.current.delete(index);
                        }
                      }}
                      playerData={player}
                      round={round}
                      draft_position={actualPick}
                      draftedIds={draftedIds} // Pass only live drafted IDs
                      manuallyDraftedIds={manuallyDraftedIds} // Pass manually drafted IDs separately
                      scoringType={scoringType}
                      tierCounts={tierCounts} // Pass tier counts for scarcity calculation
                      onDraftToggle={() =>
                        player &&
                        player.sleeper_id &&
                        handlePlayerDraft(player.sleeper_id)
                      } // Use sleeper_id for manual drafting
                      positionColors={positionColors} // Pass dynamic position colors
                      teamCount={teamCount} // Pass dynamic team count
                      highlightedPlayer={effectiveHighlighted} // Pass highlighted player name
                    />
                  );
                })}
              </div>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Bottom controls: Drawer on mobile, sticky inline on MD+ */}
      {isMdUp ? (
        <Box
          sx={{
            flex: '0 0 auto',
            height: (drawerState === 'open' ? "50vh" : drawerState === 'medium' ? "35vh" : "72px"),
            backgroundColor: colors.primary[500],
            borderTop: `1px solid ${colors.primary[600]}`,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              px: { xs: 1, md: 2 },
              py: 1,
              display: "flex",
              alignItems: "center",
              borderBottom: `1px solid ${colors.primary[600]}`,
            }}
          >
            <Box
              sx={{
                flex: "0 1 auto",
                minWidth: 120,
                maxWidth: { xs: 140, sm: 0, md: 200, lg: 240, xl: 280 },
                display: { xs: "none", sm: "none", md: "block" },
              }}
            >
              <SearchBar
                playerPool={playerPool}
                draftedIds={draftedIds}
                customDraftAssignments={customDraftAssignments}
                manuallyDraftedIds={manuallyDraftedIds}
                onHighlightPlayer={setHighlightedPlayerLocal}
                positionColors={positionColors}
              />
            </Box>
            <IconButton
              onClick={() => setSearchOpen(true)}
              sx={{
                color: colors.grey[100],
                display: { xs: "inline-flex", sm: "inline-flex", md: "none" },
                mr: 0.5,
                minWidth: '48px',
                minHeight: '48px',
                padding: '12px',
              }}
              aria-label="Search players"
            >
              <SearchIcon />
            </IconButton>
            <Box
              sx={{
                flex: "1 1 auto",
                minWidth: 0,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <DraftboardControls
                scoringType={scoringType}
                platform={"sleeper"}
                draftId={draftId}
                positionFilters={positionFilters}
                handlePositionFilterChange={handlePositionFilterChange}
                positionColors={positionColors}
                sortOption={sortOption}
                handleSortChange={handleSortChange}
                isLiveMode={isLiveMode}
                isConnected={isConnected}
                handleLiveModeChange={handleLiveModeChange}
                connectLiveDraft={connectLiveDraft}
                context="drawer"
              />
            </Box>
            <Box
              sx={{
                flex: "0 0 auto",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <IconButton
                onClick={() => {
                  if (drawerState === 'collapsed') {
                    setDrawerState('medium');
                  } else if (drawerState === 'medium') {
                    setDrawerState('open');
                  } else {
                    setDrawerState('medium');
                  }
                }}
                sx={{
                  color: colors.grey[100],
                  minWidth: '44px',
                  minHeight: '44px',
                  padding: '10px',
                }}
                aria-label={
                  drawerState === 'collapsed' 
                    ? "Expand drawer to medium" 
                    : drawerState === 'medium' 
                    ? "Expand drawer fully" 
                    : "Collapse drawer to medium"
                }
              >
                <ExpandLessIcon />
              </IconButton>
              <IconButton
                onClick={() => {
                  if (drawerState === 'open') {
                    setDrawerState('medium');
                  } else if (drawerState === 'medium') {
                    setDrawerState('collapsed');
                  } else {
                    setDrawerState('medium');
                  }
                }}
                sx={{
                  color: colors.grey[100],
                  minWidth: '44px',
                  minHeight: '44px',
                  padding: '10px',
                }}
                aria-label={
                  drawerState === 'open' 
                    ? "Collapse drawer to medium" 
                    : drawerState === 'medium' 
                    ? "Collapse drawer fully" 
                    : "Expand drawer to medium"
                }
              >
                <ExpandMoreIcon />
              </IconButton>
            </Box>
          </Box>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              px: { xs: 1, md: 2 },
              py: 1,
              display: drawerState !== 'collapsed' ? "grid" : "none",
              gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
              gap: 1,
            }}
          >
            <PositionList
              title="QB"
              players={getPositionPlayers("QB")}
              color={positionColors.QB}
              highlightedPlayer={effectiveHighlighted}
            />
            <PositionList
              title="RB"
              players={getPositionPlayers("RB")}
              color={positionColors.RB}
              highlightedPlayer={effectiveHighlighted}
            />
            <PositionList
              title="WR"
              players={getPositionPlayers("WR")}
              color={positionColors.WR}
              highlightedPlayer={effectiveHighlighted}
            />
            <PositionList
              title="TE"
              players={getPositionPlayers("TE")}
              color={positionColors.TE}
              highlightedPlayer={effectiveHighlighted}
            />
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            flex: '0 0 auto',
            height: drawerState === 'open' ? "50vh" : drawerState === 'medium' ? "35vh" : "72px",
            backgroundColor: colors.primary[500],
            borderTop: `1px solid ${colors.primary[600]}`,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              px: { xs: 1, md: 2 },
              py: 1,
              display: "flex",
              alignItems: "center",
              borderBottom: `1px solid ${colors.primary[600]}`,
            }}
          >
            <Box
              sx={{
                flex: "0 1 auto",
                minWidth: 120,
                maxWidth: { xs: 140, sm: 0, md: 200, lg: 240, xl: 280 },
                display: { xs: "none", sm: "none", md: "block" },
              }}
            >
              <SearchBar
                playerPool={playerPool}
                draftedIds={draftedIds}
                customDraftAssignments={customDraftAssignments}
                manuallyDraftedIds={manuallyDraftedIds}
                onHighlightPlayer={setHighlightedPlayerLocal}
                positionColors={positionColors}
              />
            </Box>
            {/* XS/SM search icon opens search drawer */}
            <IconButton
              onClick={() => setSearchOpen(true)}
              sx={{
                color: colors.grey[100],
                display: { xs: "inline-flex", sm: "inline-flex", md: "none" },
                mr: 0.5,
                minWidth: '48px',
                minHeight: '48px',
                padding: '12px',
              }}
              aria-label="Search players"
            >
              <SearchIcon />
            </IconButton>
            {/* Center group: Position filters + ADP/ECR */}
            <Box
              sx={{
                flex: "1 1 auto",
                minWidth: 0,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <DraftboardControls
                scoringType={scoringType}
                platform={"sleeper"}
                draftId={draftId}
                positionFilters={positionFilters}
                handlePositionFilterChange={handlePositionFilterChange}
                positionColors={positionColors}
                sortOption={sortOption}
                handleSortChange={handleSortChange}
                isLiveMode={isLiveMode}
                isConnected={isConnected}
                handleLiveModeChange={handleLiveModeChange}
                connectLiveDraft={connectLiveDraft}
                context="drawer"
              />
            </Box>
            <Box
              sx={{
                flex: "0 0 auto",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <IconButton
                onClick={() => {
                  if (drawerState === 'collapsed') {
                    setDrawerState('medium');
                  } else if (drawerState === 'medium') {
                    setDrawerState('open');
                  } else {
                    setDrawerState('medium');
                  }
                }}
                sx={{
                  color: colors.grey[100],
                  minWidth: '44px',
                  minHeight: '44px',
                  padding: '10px',
                }}
                aria-label={
                  drawerState === 'collapsed' 
                    ? "Expand drawer to medium" 
                    : drawerState === 'medium' 
                    ? "Expand drawer fully" 
                    : "Collapse drawer to medium"
                }
              >
                <ExpandLessIcon />
              </IconButton>
              <IconButton
                onClick={() => {
                  if (drawerState === 'open') {
                    setDrawerState('medium');
                  } else if (drawerState === 'medium') {
                    setDrawerState('collapsed');
                  } else {
                    setDrawerState('medium');
                  }
                }}
                sx={{
                  color: colors.grey[100],
                  minWidth: '44px',
                  minHeight: '44px',
                  padding: '10px',
                }}
                aria-label={
                  drawerState === 'open' 
                    ? "Collapse drawer to medium" 
                    : drawerState === 'medium' 
                    ? "Collapse drawer fully" 
                    : "Expand drawer to medium"
                }
              >
                <ExpandMoreIcon />
              </IconButton>
            </Box>
          </Box>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              px: { xs: 1, md: 2 },
              py: 1,
              display: drawerState !== 'collapsed' ? "grid" : "none",
              gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
              gap: 1,
            }}
          >
            <PositionList
              title="QB"
              players={getPositionPlayers("QB")}
              color={positionColors.QB}
              highlightedPlayer={effectiveHighlighted}
            />
            <PositionList
              title="RB"
              players={getPositionPlayers("RB")}
              color={positionColors.RB}
              highlightedPlayer={effectiveHighlighted}
            />
            <PositionList
              title="WR"
              players={getPositionPlayers("WR")}
              color={positionColors.WR}
              highlightedPlayer={effectiveHighlighted}
            />
            <PositionList
              title="TE"
              players={getPositionPlayers("TE")}
              color={positionColors.TE}
              highlightedPlayer={effectiveHighlighted}
            />
          </Box>
        </Box>
      )}

      {/* Mobile Search Drawer */}
      <Drawer
        anchor="bottom"
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: colors.primary[500],
            borderTopLeftRadius: '16px',
            borderTopRightRadius: '16px',
            height: '80vh',
            maxHeight: '80vh',
          }
        }}
        style={{ 
          zIndex: 1300 // Above the permanent drawer
        }}
      >
        {/* Drag handle */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          pt: 1, 
          pb: 0 
        }}>
          <Box sx={{ 
            width: '36px', 
            height: '4px', 
            backgroundColor: colors.grey[400], 
            borderRadius: '2px' 
          }} />
        </Box>
        
        <Box sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}>
          <Box sx={{
            position: 'sticky',
            top: 8,
            zIndex: 1,
          }}>
            <SearchBar
              playerPool={playerPool}
              draftedIds={draftedIds}
              customDraftAssignments={customDraftAssignments}
              manuallyDraftedIds={manuallyDraftedIds}
              onHighlightPlayer={handleHighlightPlayer}
              onPlayerSelected={handlePlayerSelected}
              autoFocus
              positionColors={positionColors}
            />
          </Box>
          <Box sx={{ flex: 1 }} />
        </Box>
      </Drawer>
    </Box>
  );
}

export default Draftboard;
